import type { Invoice } from "@core/types";
import type { EvmCall, PayBuilders } from "./builders";
import type { PublicInvoice } from "../lib/types";

// The chain builders only need the payment terms; merchantId is server-side and never public.
const asInvoice = (inv: PublicInvoice): Invoice => ({ ...inv, merchantId: "" }) as Invoice;

const erc20AllowanceAbi = [
  {
    type: "function",
    name: "allowance",
    stateMutability: "view",
    inputs: [
      { name: "owner", type: "address" },
      { name: "spender", type: "address" },
    ],
    outputs: [{ name: "", type: "uint256" }],
  },
] as const;

/**
 * Real transaction builders. Chain packages are imported on first use so they stay out of the main
 * bundle. Both return unsigned transactions for the payer's own wallet to sign and send.
 */
export function createPayBuilders(baseRouterAddress: string | null): PayBuilders {
  return {
    solana: {
      async buildWalletPayTransaction({ invoice, payer, prep }) {
        const sol = await import("@mikodes-checkout/solana");
        const { transaction } = await sol.buildWalletPayTransaction({
          invoice: asInvoice(invoice),
          payer,
          prep: sol.prepFromRpc(prep),
          // A partially paid invoice: pay only what is still missing.
          paidSoFar: invoice.status === "partially_paid" ? (invoice.paidSoFar ?? null) : null,
        });
        return transaction;
      },
    },
    evm: baseRouterAddress
      ? {
          async buildPayCalls({ invoice, payer, publicClient }) {
            const evm = await import("@mikodes-checkout/evm");
            const inv = asInvoice(invoice);
            const calls: EvmCall[] = [];
            const allowance = await publicClient.readContract({
              address: inv.asset.address as `0x${string}`,
              abi: erc20AllowanceAbi,
              functionName: "allowance",
              args: [payer, baseRouterAddress as `0x${string}`],
            });
            if (allowance < BigInt(inv.amount)) {
              const a = evm.buildApproveCall(inv, baseRouterAddress);
              calls.push({ kind: "approve", to: a.to, data: a.data });
            }
            const p = evm.buildPayCall(inv, baseRouterAddress);
            calls.push({ kind: "pay", to: p.to, data: p.data });
            return calls;
          },
        }
      : null,
  };
}
